import { useState } from "react";
import { FlaskConical, Loader2, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { patchAdminBrowserTest } from "@/lib/api";

/**
 * ElevenLabs Browser-test toggle (super-admin only).
 * Reads live status from the company's `elevenlabs` projection (never local
 * assumptions) and toggles the backend flag via the existing endpoint:
 *   PATCH /admin/companies/{id}/integrations/elevenlabs/browser-test { enabled }
 * Shows read-only integration info + a loud warning while testmode is ON.
 * Never renders secrets/hashes.
 */
export default function AdminBrowserTestCard({ el = {}, company = {}, companyId, onReload }) {
    const [busy, setBusy] = useState(false);
    const on = !!el.browser_test_enabled;

    const toggle = async () => {
        setBusy(true);
        try {
            await patchAdminBrowserTest(companyId, !on);
            toast.success(!on ? "Browser-test slået TIL" : "Browser-test slået FRA");
            await onReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke ændre browser-test");
        } finally {
            setBusy(false);
        }
    };

    const webhookStatus = (configured) =>
        configured ? (
            <span className="text-[11px] font-semibold text-emerald-700">● Klar</span>
        ) : (
            <span className="text-[11px] font-semibold text-slate-400">○ Ikke sat</span>
        );

    return (
        <div
            className={`rounded-2xl border-2 overflow-hidden transition-colors ${on ? "border-amber-400 bg-amber-50/60" : "border-slate-200 bg-white"}`}
            data-testid="browser-test-card"
        >
            <header className="px-5 py-3 flex items-center gap-2 border-b border-slate-100">
                <FlaskConical className={`w-4 h-4 ${on ? "text-amber-700" : "text-slate-500"}`} />
                <p className="text-sm font-bold text-slate-900 flex-1">ElevenLabs Browser-test</p>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">kun super-admin</span>
            </header>

            <div className="p-5 space-y-4">
                <p className="text-xs text-slate-600 leading-relaxed">
                    Tillader ElevenLabs Browser Preview at teste Voice Intake end-to-end uden et fysisk
                    telefonopkald. Brug kun under test.
                </p>

                {/* Status + toggle */}
                <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">Browser-test</p>
                        <p className="text-sm font-semibold mt-0.5" data-testid="browser-test-status">
                            {on ? (
                                <span className="text-amber-700 inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> Aktiv
                                </span>
                            ) : (
                                <span className="text-slate-500 inline-flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-slate-300" /> Deaktiveret
                                </span>
                            )}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={toggle}
                        disabled={busy}
                        data-testid="browser-test-toggle"
                        aria-pressed={on}
                        className={`relative inline-flex h-8 w-16 shrink-0 items-center rounded-full border transition disabled:opacity-50 ${on ? "bg-amber-500 border-amber-500" : "bg-slate-200 border-slate-300"}`}
                    >
                        <span
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transform transition ${on ? "translate-x-9" : "translate-x-1"}`}
                        >
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
                        </span>
                        <span className={`absolute text-[10px] font-bold ${on ? "left-2 text-white" : "right-2 text-slate-500"}`}>
                            {on ? "ON" : "OFF"}
                        </span>
                    </button>
                </div>

                {/* Loud warning when ON */}
                {on && (
                    <div className="rounded-xl border-2 border-amber-400 bg-amber-100 p-3 flex items-start gap-2" data-testid="browser-test-warning">
                        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-900">
                            <p className="font-bold uppercase tracking-wide text-amber-800">Testmode aktiv</p>
                            <p className="mt-1">
                                ElevenLabs Browser Preview kan oprette <strong>testkunder og testjobs</strong> direkte i
                                denne virksomheds database. Slå testmode fra igen efter testen.
                            </p>
                            <p className="mt-1.5 text-amber-700">Syntetiske admissions udløber automatisk efter 30 min.</p>
                        </div>
                    </div>
                )}

                {/* Read-only integration info (never secrets) */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 divide-y divide-slate-100 text-xs" data-testid="browser-test-integration-info">
                    <InfoRow label="Virksomhed" value={company.name || "—"} />
                    <InfoRow label="Agent" value={el.agent_id_masked || "—"} mono />
                    <InfoRow label="Public integration ID" value={el.public_integration_id_masked || "—"} mono />
                    <div className="px-4 py-2.5 flex items-center justify-between">
                        <span className="text-slate-500">Pre-call</span>
                        {webhookStatus(el.pre_call_configured)}
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between">
                        <span className="text-slate-500">Voice Intake</span>
                        {webhookStatus(el.voice_intake_configured)}
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between">
                        <span className="text-slate-500">Browser-test</span>
                        <span className={`text-[11px] font-semibold ${on ? "text-amber-700" : "text-slate-400"}`}>
                            {on ? "ON" : "OFF"}
                        </span>
                    </div>
                </div>

                <p className="text-[10px] text-slate-400 inline-flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3" /> Secrets vises aldrig her.
                </p>
            </div>
        </div>
    );
}

function InfoRow({ label, value, mono }) {
    return (
        <div className="px-4 py-2.5 flex items-center justify-between gap-3">
            <span className="text-slate-500 shrink-0">{label}</span>
            <span className={`text-slate-800 font-medium truncate ${mono ? "font-mono" : ""}`} title={value}>{value}</span>
        </div>
    );
}
